import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { IconBtn, fmtDuration } from "../../ui";

const BARS = 72;
const SPEEDS = [1, 1.5, 2];

/**
 * What the stored waveform holds, or null if it holds anything else.
 *
 * The field arrives from a Json column as `unknown`, so it is checked here
 * rather than trusted - the browser mirror of `readStoredPeaks` on the server.
 * The array's own length is what gets drawn, never `BARS`: the bar count is a
 * wire contract now, and a row measured under a different one must keep
 * drawing rather than come out truncated.
 */
function readPeaks(raw: unknown): number[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out: number[] = [];
  for (const value of raw) {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    out.push(Math.max(0, Math.min(100, value)));
  }
  return out;
}

/**
 * Fallback bar heights, for a recording nobody measured.
 *
 * NOT the audio's amplitude: a stable hash of the call id, so the shape looks
 * like a waveform, stays identical between renders, and differs between calls.
 * A scrubber wearing a waveform's clothes, which is what it has to be good at.
 *
 * This was the only branch until `recordingPeaks` arrived, and it stays because
 * null is permanent for a whole class of rows: every recording stored before
 * that column existed has none and nothing re-reads stored audio, the
 * server-side measurement fails open, and a clip too quiet to describe is
 * refused rather than guessed at. Drawing nothing in those three cases would
 * read as a broken player.
 */
function bars(seed: string): number[] {
  let h = 7;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return Array.from({ length: BARS }, () => {
    h = (h * 1103515245 + 12345) >>> 0;
    return 22 + ((h >>> 8) % 78);
  });
}

/**
 * What the pane can ask of the player from outside.
 *
 * One method, because one is what the transcript needs: the operator clicks a
 * line and the playhead goes there. Deliberately NOT play/pause - a timestamp
 * click moves the playhead and nothing else, so a click never starts sound in
 * an open-plan office. A player already running keeps running, which is the
 * behaviour somebody scrubbing through a call actually wants.
 */
export interface RecordingPlayerHandle {
  /** Put the playhead at this second. Clamped, and queued if the audio is not
   *  ready to be seeked yet. */
  seekTo(seconds: number): void;
}

/** `HTMLMediaElement.HAVE_METADATA` - duration known, seeking possible. */
const HAVE_METADATA = 1;

/** Recording playback. A plain <audio> works because the audio route is
 *  same-origin and cookie-authed - no signed URLs involved. */
export const RecordingPlayer = forwardRef<
  RecordingPlayerHandle,
  {
    callId: string;
    duration: number | null;
    /** The server's measurement of this recording. Unvalidated on purpose - see
     *  `readPeaks`. Absent or malformed both fall back to the hash. */
    peaks?: unknown;
    /**
     * Which of the call's recordings to play. Absent means the row's slot,
     * which is every ordinary call.
     *
     * "agent" is the AI conversation on a call where a message displaced it -
     * see `agentRecordingKey`. It reaches the same route, under the same
     * ownership check and the same listening trail, so the only thing that
     * changes here is the URL.
     */
    part?: "agent";
  }
>(function RecordingPlayer({ callId, duration, peaks, part }, ref) {
  const t = useT();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [total, setTotal] = useState(duration ?? 0);
  const [speed, setSpeed] = useState(0);

  /**
   * A seek that arrived before the audio could take it.
   *
   * ⚠ THIS QUEUE IS THE WHOLE REASON THE HANDLE IS NOT A ONE-LINER. The element
   * is `preload="metadata"`, so for the first moment after the pane opens it has
   * no duration and cannot be seeked - and the operator clicking a timestamp in
   * that moment is not doing anything unusual, it is the first thing they do.
   * Writing `currentTime` then is dropped by the browser without an error, so
   * the click would simply do nothing and look like a dead control. Held here
   * instead and applied by `onLoadedMetadata`.
   */
  const queued = useRef<number | null>(null);

  // The seed carries the part, so the two recordings of one call do not draw
  // the same fallback waveform - two identical shapes under two players would
  // read as one file shown twice.
  const heights = useMemo(
    () => readPeaks(peaks) ?? bars(part ? `${callId}:${part}` : callId),
    [peaks, callId, part],
  );
  const src =
    `/api/admin/crm/calls/${encodeURIComponent(callId)}/audio` +
    (part ? `?part=${encodeURIComponent(part)}` : "");

  // A different call is a different recording: reset rather than carry the last
  // one's position into it. The queue goes too - a seek meant for the previous
  // call would otherwise land on this one the moment its metadata arrives.
  // `part` is in here for the same reason `callId` is: it names a different
  // file, and on the same call.
  useEffect(() => {
    setPlaying(false);
    setAt(0);
    setTotal(duration ?? 0);
    queued.current = null;
  }, [callId, duration, part]);

  useImperativeHandle(ref, () => ({ seekTo }));

  useEffect(() => {
    const el = audioRef.current;
    if (el) el.playbackRate = SPEEDS[speed]!;
  }, [speed]);

  // Off the drawn length, not `BARS`: a stored waveform decides its own bar
  // count, and measuring progress against the constant would colour the wrong
  // number of bars the moment those two disagree.
  const played = total > 0 ? Math.round((at / total) * heights.length) : 0;

  /**
   * Move the playhead, with the span to clamp against passed in.
   *
   * The span is an argument rather than read off `total` because the one caller
   * that matters most does not have `total` yet: `onLoadedMetadata` has the
   * duration in hand and `setTotal` has not landed in state, so reading state
   * there would clamp the queued seek against zero and send it to the start.
   */
  function moveTo(seconds: number, span: number) {
    const el = audioRef.current;
    if (!el || !(span > 0)) return;
    const next = Math.min(span, Math.max(0, seconds));
    el.currentTime = next;
    setAt(next);
  }

  function seekTo(seconds: number) {
    const el = audioRef.current;
    // `readyState`, not `total`: `total` is seeded from the row's stored length,
    // so it is usually non-zero long before the browser can act on it. Asking
    // the element whether it can be seeked is the only honest question.
    if (!el || el.readyState < HAVE_METADATA) {
      queued.current = seconds;
      return;
    }
    moveTo(seconds, Number.isFinite(el.duration) && el.duration > 0 ? el.duration : total);
  }

  function seek(e: React.MouseEvent<HTMLDivElement>) {
    // A fraction of nothing is not a position: without a span this click cannot
    // be turned into a second, so there is nothing to queue either.
    if (!total) return;
    const box = e.currentTarget.getBoundingClientRect();
    seekTo(((e.clientX - box.left) / box.width) * total);
  }

  /** The bar announces itself as a slider, so it has to behave like one. Arrows
   *  step five seconds, Home/End jump to the edges, space toggles playback. */
  function onKey(e: React.KeyboardEvent<HTMLDivElement>) {
    const step = e.shiftKey ? 30 : 5;
    if (e.key === "ArrowLeft") seekTo(at - step);
    else if (e.key === "ArrowRight") seekTo(at + step);
    else if (e.key === "Home") seekTo(0);
    else if (e.key === "End") seekTo(total);
    else if (e.key === " " || e.key === "Enter") toggle();
    else return;
    e.preventDefault();
  }

  function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (playing) el.pause();
    else void el.play();
    setPlaying(!playing);
  }

  return (
    <div className="crm-player">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          const span = Number.isFinite(d) && d > 0 ? d : total;
          if (span !== total) setTotal(span);
          // The queued click, now that there is something to seek. Cleared
          // whether or not it lands, so a recording with no readable duration
          // does not keep one stale position waiting forever.
          const want = queued.current;
          queued.current = null;
          if (want !== null) moveTo(want, span);
        }}
        onTimeUpdate={(e) => setAt(e.currentTarget.currentTime)}
        onEnded={() => setPlaying(false)}
        className="hidden"
      />

      <button
        type="button"
        className="crm-play"
        onClick={toggle}
        aria-label={t(playing ? "calls.player.pause" : "calls.player.play")}
      >
        {playing ? <Pause className="size-4 fill-current" /> : <Play className="ml-0.5 size-4 fill-current" />}
      </button>

      <div
        className="crm-wave"
        onClick={seek}
        onKeyDown={onKey}
        role="slider"
        aria-label={t("calls.detail.recording")}
        aria-valuenow={Math.round(at)}
        aria-valuemin={0}
        aria-valuemax={Math.round(total)}
        tabIndex={0}
      >
        {heights.map((h, i) => (
          <i key={i} style={{ height: `${h}%` }} className={cn(i < played && "is-played")} />
        ))}
      </div>

      <span className="crm-ptime">
        {fmtDuration(at)} / {fmtDuration(total)}
      </span>

      <IconBtn wide onClick={() => setSpeed((s) => (s + 1) % SPEEDS.length)} title={t("calls.player.speed")}>
        {SPEEDS[speed]}x
      </IconBtn>
    </div>
  );
});
